'use client';

import React, { useEffect } from 'react';
import { useTheme } from 'next-themes';

import { Button } from '@ui';
import { IconMoon, IconSun } from '@icons';

export type ThemeName = 'light' | 'dark' | 'blue-gray' | 'blue-teal' | 'blue-green';

export interface ThemeToggleViewProps {
  theme: ThemeName;
  onClick: () => void;
}

/** Presentational toggle (icons + click). Use in Storybook/tests without `next-themes`. */
export function ThemeToggleView({ theme, onClick }: ThemeToggleViewProps) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  return (
    <Button
      className='theme-btn'
      id='themeToggle'
      aria-label='Toggle theme'
      as='button'
      type='button'
      onClick={onClick}
      label={
        <span suppressHydrationWarning>
          {mounted ? (
            theme === 'light' ? (
              <IconMoon />
            ) : theme === 'dark' ? (
              <IconSun />
            ) : (
              <IconMoon />
            )
          ) : (
            <IconMoon />
          )}
        </span>
      }
    />
  );
}

export interface ThemeToggleProps {
  /**
   * Called when `next-themes` resolves to `light` or `dark` (e.g. after hydration).
   * Host apps use this for cookies, server hints, or analytics—this module stays storage-agnostic.
   */
  onResolvedThemeChange?: (theme: 'light' | 'dark') => void;
}

/**
 * Wired toggle: `next-themes` + optional {@link ThemeToggleProps.onResolvedThemeChange}, then {@link ThemeToggleView}.
 * Render inside {@link ThemeProvider} (e.g. root layout).
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({ onResolvedThemeChange }) => {
  const { resolvedTheme, setTheme } = useTheme();

  useEffect(() => {
    if (resolvedTheme === 'light' || resolvedTheme === 'dark') {
      onResolvedThemeChange?.(resolvedTheme);
    }
  }, [resolvedTheme, onResolvedThemeChange]);

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const themeForToggle: 'light' | 'dark' = resolvedTheme === 'dark' ? 'dark' : 'light';

  return <ThemeToggleView theme={themeForToggle} onClick={toggleTheme} />;
};

export default ThemeToggle;
